import { useQuery } from '@tanstack/react-query';
import { Bot, ExternalLink, Sparkles } from 'lucide-react';
import { Card, CardBody } from '@/components/Card';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { LinkDoArtigo } from '@/features/knowledge/navegacao';
import { chaves, obterSugestoesIa } from '@/features/tickets/tickets.api';

const PRIORIDADE_COR: Record<string, string> = {
  P1: 'text-destructive',
  P2: 'text-warning',
  P3: 'text-warning/70',
  P4: 'text-muted-foreground',
};

const TIPO_PT: Record<string, string> = {
  INCIDENT: 'Incidente',
  REQUEST: 'Solicitação',
  PROBLEM: 'Problema',
  CHANGE: 'Mudança',
};

/**
 * Painel de sugestões de IA (Fase 9).
 *
 * Carrega automaticamente ao abrir o chamado. Mostra:
 *   - Artigos encontrados por similaridade vetorial (se OpenAI configurado).
 *   - Categoria, prioridade e tipo sugeridos pelo LLM (se Anthropic configurado).
 *
 * Se nenhuma chave de IA estiver configurada no backend, o painel fica invisível.
 */
export function PainelDeIa({ ticketId }: { ticketId: string }) {
  const { data, isPending } = useQuery({
    queryKey: chaves.sugestoesIa(ticketId),
    queryFn: () => obterSugestoesIa(ticketId),
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (isPending) {
    return (
      <SkeletonRegion label="Analisando com IA">
        <Skeleton className="h-14 w-full rounded-lg" />
      </SkeletonRegion>
    );
  }

  const temArtigos = (data?.articles?.length ?? 0) > 0;
  const temLlm =
    data?.llm &&
    (data.llm.suggestedCategory || data.llm.suggestedPriority || data.llm.suggestedType);

  if (!temArtigos && !temLlm) return null;

  return (
    <section aria-labelledby="titulo-ia" className="space-y-2">
      <h2
        id="titulo-ia"
        className="flex items-center gap-1.5 text-sm font-semibold text-foreground"
      >
        <Sparkles className="h-4 w-4 text-primary" aria-hidden />
        Análise de IA
      </h2>

      {temLlm && data?.llm && (
        <Card>
          <CardBody className="py-3">
            <div className="flex items-start gap-2">
              <Bot className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap gap-3 text-sm">
                  {data.llm.suggestedCategory && (
                    <span>
                      <span className="text-muted-foreground">Categoria: </span>
                      <span className="font-medium">{data.llm.suggestedCategory}</span>
                    </span>
                  )}
                  {data.llm.suggestedPriority && (
                    <span>
                      <span className="text-muted-foreground">Prioridade: </span>
                      <span
                        className={`font-semibold ${PRIORIDADE_COR[data.llm.suggestedPriority] ?? ''}`}
                      >
                        {data.llm.suggestedPriority}
                      </span>
                    </span>
                  )}
                  {data.llm.suggestedType && (
                    <span>
                      <span className="text-muted-foreground">Tipo: </span>
                      <span className="font-medium">
                        {TIPO_PT[data.llm.suggestedType] ?? data.llm.suggestedType}
                      </span>
                    </span>
                  )}
                </div>
                {data.llm.rationale && (
                  <p className="text-xs text-muted-foreground">{data.llm.rationale}</p>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {temArtigos && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">
            Artigos relacionados por similaridade
          </p>
          <ul className="space-y-1">
            {data?.articles.map((a) => (
              <li
                key={a.id}
                className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <LinkDoArtigo
                      artigoId={a.id}
                      className="font-mono text-xs text-muted-foreground hover:text-primary"
                    >
                      {a.code}
                    </LinkDoArtigo>
                    <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                      {a.score}% similar
                    </span>
                  </div>
                  <p className="truncate text-sm font-medium text-foreground">{a.title}</p>
                  <p className="line-clamp-1 text-xs text-muted-foreground">{a.summary}</p>
                </div>
                <LinkDoArtigo artigoId={a.id} className="mt-0.5 shrink-0 text-muted-foreground hover:text-primary">
                  <ExternalLink className="h-3.5 w-3.5" aria-label="Abrir artigo" />
                </LinkDoArtigo>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
